import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Search, Bell, Sun, Moon, Briefcase, ArrowLeftRight, Gift } from 'lucide-react';
import Sidebar from './Sidebar';
import { useTheme } from '../lib/theme';
import { useAuth } from '../lib/auth';
import { useBilling } from '../lib/billing';
import { switchWorkspaceAndReload } from '../lib/plans';

interface LayoutProps {
  children: React.ReactNode;
}

const TITLES: Record<string, [string, string]> = {
  '/dashboard': ['Dashboard', 'Your lead-sync command center'],
  '/webhooks': ['Webhooks', 'Inbound lead capture endpoints'],
  '/forms': ['Forms', 'Embeddable lead-capture forms'],
  '/quizzes': ['Quizzes', 'Interactive scored quizzes'],
  '/buttons': ['Buttons', 'Embeddable lead-capture buttons'],
  '/popups': ['Popups', 'One-script lead-capture modals'],
  '/ai-connector': ['AI Connector', 'Automate with AI workflows'],
  '/settings': ['Settings', 'Account & integrations'],
  '/billing': ['Billing', 'Plan, usage & subscription'],
  '/workspaces': ['Workspaces', 'Client workspaces for your agency'],
  '/vault': ['Vault Dashboard', 'Backups, snapshots & connection health'],
  '/vault/connections': ['Connections', 'Linked marketing platforms'],
  '/vault/backups': ['Backups', 'Run & monitor backup jobs'],
  '/vault/snapshots': ['Snapshots', 'Stored vault snapshots'],
  '/vault/restores': ['Restores', 'Restore data to your platforms'],
  '/vault/schedules': ['Schedules', 'Automated backup cadence'],
  '/admin/homepage': ['Homepage Editor', 'Marketing site content'],
  '/admin/users': ['User Management', 'Team & permissions'],
  '/admin/system-settings': ['System Settings', 'Platform configuration'],
};

function resolveTitle(pathname: string): [string, string] {
  if (TITLES[pathname]) return TITLES[pathname];
  if (pathname.startsWith('/vault/snapshots/')) return ['Data Browser', 'Browse snapshot contents'];
  const match = Object.keys(TITLES)
    .filter((k) => pathname.startsWith(k))
    .sort((a, b) => b.length - a.length)[0];
  return match ? TITLES[match] : ['My Lead Syncer', ''];
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [title, desc] = resolveTitle(location.pathname);
  const { user } = useAuth();
  const { billing } = useBilling();
  const [switching, setSwitching] = React.useState(false);
  const [switchError, setSwitchError] = React.useState<string | null>(null);

  const switchBack = async () => {
    setSwitching(true);
    setSwitchError(null);
    const err = await switchWorkspaceAndReload(null);
    if (err) { setSwitchError(err); setSwitching(false); }
  };

  return (
    <div className="min-h-screen bg-bg">
      <Sidebar />
      <div className="md:pl-[252px] flex flex-col min-h-screen">
        {user?.actor && (
          <div className="flex items-center gap-3 flex-wrap px-4 md:px-[26px] py-2 pl-14 md:pl-[26px] bg-accent-soft border-b border-line text-accent-ink text-[13px] font-semibold">
            <Briefcase className="w-4 h-4 flex-shrink-0" />
            <span className="min-w-0">
              Working in <strong className="font-bold">{user.workspace?.name ?? 'a client workspace'}</strong>
            </span>
            {switchError && <span className="text-danger font-medium">{switchError}</span>}
            <button
              onClick={switchBack}
              disabled={switching}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-surface border border-line text-ink hover:border-accent transition disabled:opacity-60"
            >
              <ArrowLeftRight className="w-3.5 h-3.5" />
              {switching ? 'Switching…' : 'Switch back to agency'}
            </button>
          </div>
        )}
        {billing?.trialAvailable && location.pathname !== '/billing' && (
          <div className="flex items-center gap-3 flex-wrap px-4 md:px-[26px] py-2 pl-14 md:pl-[26px] bg-accent-soft border-b border-line text-accent-ink text-[13px] font-semibold">
            <Gift className="w-4 h-4 flex-shrink-0" />
            <span className="min-w-0">Your free 14-day Starter trial is waiting — no card needed.</span>
            <Link
              to="/billing"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-surface border border-line text-ink hover:border-accent transition"
            >
              Activate trial
            </Link>
          </div>
        )}
        {/* Topbar */}
        <header
          className="sticky top-0 z-20 h-[66px] flex items-center gap-4 px-4 md:px-[26px] border-b border-line"
          style={{ background: 'var(--topbar)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}
        >
          <div className="min-w-0 pl-10 md:pl-0">
            <h1 className="m-0 font-display font-bold text-[18px] text-ink tracking-tight truncate">{title}</h1>
            {desc && <div className="font-medium text-[12px] text-ink-3 mt-px truncate">{desc}</div>}
          </div>
          <div className="flex-1" />
          <div className="hidden sm:flex items-center gap-2 h-[38px] px-3 rounded-[10px] bg-surface-2 border border-line min-w-[210px] text-ink-3">
            <Search className="w-4 h-4 flex-shrink-0" />
            <input
              placeholder="Search…"
              className="border-none bg-transparent outline-none font-medium text-[13px] text-ink w-full"
            />
            <span className="font-semibold text-[10px] border border-line rounded-[5px] px-1.5 py-px text-ink-3">⌘K</span>
          </div>
          <button
            onClick={toggleTheme}
            title="Toggle theme"
            className="w-[38px] h-[38px] rounded-[10px] bg-surface-2 border border-line text-ink-2 flex items-center justify-center hover:text-accent hover:border-accent transition"
          >
            {theme === 'dark' ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
          </button>
          <button
            className="relative w-[38px] h-[38px] rounded-[10px] bg-surface-2 border border-line text-ink-2 flex items-center justify-center hover:text-ink transition"
            title="Notifications"
          >
            <Bell className="w-[18px] h-[18px]" />
            <span className="absolute top-[9px] right-[9px] w-[7px] h-[7px] rounded-full bg-cyan border-2 border-[var(--surface-2)]" />
          </button>
        </header>

        <main className="flex-1 p-4 md:p-[26px]">{children}</main>
      </div>
    </div>
  );
}
